// src/services/api.ts
import axios from "axios";

/* =========================================
  Env & Config (URL absoluta SIEMPRE)
  ========================================= */
const API_URL = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
if (!API_URL) throw new Error("Falta REACT_APP_API_URL");

console.log("[api] BASE_URL =", API_URL);

/** Minutos de margen para auto-logout antes de exp real (opcional) */
const EARLY_EXIT_MIN = Number(process.env.REACT_APP_JWT_EARLY_EXIT_MIN || 1);

/* =========================================
  Tipos base (ajusta según tu backend)
  ========================================= */
export interface LoginResponseToken {
  access_token: string;
}

// Renombrado de OdeS a OC (Orden de Compra)
export interface OC {
  id: number;
  folio: string;
  created: string | null;
  updated: string | null;
  subtotal: number;
  tax: number;
  total: number;
  totalEstimado: number;
  presupuestoTotal: number;
  statusName: string;
  customerName: string;
  plate: string;
  items: {
    id: number;
    tipo: string;
    nombre: string;
    barcode: string;
    cantidad: number;
    precioUnitario: number;
    totalItem: number;
    productTypeId: number;
    productId: number;
    supplierId: number;
  }[];
  expenses: any[];
}

export interface Banco {
  id: string;
  nombre: string;
  cuenta: string;
}

export interface Proveedor {
  id: string;
  nombre: string;
  codigo?: string;
  tipo?: string;
  activo?: boolean;
  creado?: string;
  actualizado?: string;
  organizationId?: string;
}

export interface Empleado {
  id: string;
  nombre: string;
}

export interface ProductType {
  id: string;
  name: string;
}

export interface Product {
  id: string;
  name: string;
  barcode: string;
}

export interface SupplierType {
  id: string;
  name: string;
}

export interface PurchaseCategory {
  id: string;
  name: string;
}

/* =========================================
  Token helpers → SOLO sessionStorage
  ========================================= */
const TOKEN_KEY = "token";

function getToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}
function setToken(token: string) {
  sessionStorage.setItem(TOKEN_KEY, token);
}
export function clearToken() {
  sessionStorage.removeItem(TOKEN_KEY);
}

/* =========================================
  JWT helpers (auto-logout opcional)
  ========================================= */
function parseJwt(token: string): any | null {
  try {
    const [, payload] = token.split(".");
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

let logoutTimer: ReturnType<typeof setTimeout> | null = null;

export function scheduleAutoLogout(token: string, onExpire?: () => void) {
  if (logoutTimer) clearTimeout(logoutTimer);
  const payload = parseJwt(token);
  if (!payload?.exp) return;

  const now = Date.now();
  const expMs = payload.exp * 1000;
  const early = EARLY_EXIT_MIN * 60 * 1000;
  const ms = Math.max(0, expMs - early - now);

  console.log(
    "[api] scheduleAutoLogout → exp:",
    new Date(expMs).toISOString(),
    "en",
    ms,
    "ms"
  );

  logoutTimer = setTimeout(() => {
    console.warn("[api] Auto-logout por expiración del JWT");
    clearToken();
    onExpire?.();
  }, ms);
}

/* =========================================
  Axios base
  ========================================= */
const api = axios.create({
  baseURL: API_URL,
  withCredentials: false,
});

api.interceptors.request.use((config) => {
  const token = getToken();
  const method = (config.method || "get").toUpperCase();
  const url = `${config.baseURL || ""}${config.url || ""}`;

  if (token) {
    if (config.headers && typeof (config.headers as any).set === "function") {
      (config.headers as any).set("Authorization", `Bearer ${token}`);
    } else {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      } as any;
    }
  }

  console.log(`[api] → ${method} ${url}`, {
    hasAuth: Boolean(token),
    headers: config.headers,
    params: config.params,
  });

  return config;
});

api.interceptors.response.use(
  (res) => {
    const url = res.config.url || "";

    if (
      url.includes("/purchaseOrders") ||
      url.includes("/banks") ||
      url.includes("/suppliers") ||
      url.includes("/expenses")
    ) {
      console.group(
        `%c extraído de API: ${url}`,
        "color: #00ff00; background: #222; font-weight: bold;"
      );
      console.log("HTTP Status:", res.status);
      console.log("Datos Crudos (Raw):", res.data);
      console.log(
        "Estructura de llaves:",
        res.data && typeof res.data === "object" ? Object.keys(res.data) : []
      );
      console.groupEnd();
    }

    return res;
  },
  (error: unknown) => {
    if (axios.isAxiosError(error)) {
      console.error("❌ ERROR DE API CRUDO:", error.response?.data);
    }
    return Promise.reject(error);
  }
);

export function formatAxiosError(err: unknown) {
  if (axios.isAxiosError(err)) {
    return {
      isAxios: true,
      status: err.response?.status ?? null,
      url: `${err.config?.baseURL || ""}${err.config?.url || ""}`,
      method: (err.config?.method || "get").toUpperCase(),
      message: err.message,
      data: err.response?.data ?? null,
    };
  }
  return { isAxios: false, message: String(err) };
}

function isPlainObject(x: any) {
  return x && typeof x === "object" && !Array.isArray(x);
}

function pickId(obj: any): string {
  return String(
    obj?.id ??
      obj?.uuid ??
      obj?._id ??
      obj?.bankId ??
      obj?.supplierId ??
      obj?.employeeId ??
      ""
  );
}

function firstArrayInObject(raw: any): any[] {
  if (Array.isArray(raw)) return raw;

  if (isPlainObject(raw)) {
    const candidates = [
      "data",
      "items",
      "result",
      "results",
      "rows",
      "banks",
      "suppliers",
      "employees",
      "orders",
    ];

    for (const k of candidates) {
      if (Array.isArray((raw as any)[k])) return (raw as any)[k];
    }

    for (const v of Object.values(raw)) {
      if (Array.isArray(v)) return v as any[];
    }
  }

  return [];
}

export function normalizeOrder(raw: any): OC {
  console.log('api', raw);
  const totalGastado = Array.isArray(raw?.expenses)
    ? raw.expenses.reduce(
        (acc: number, e: any) => acc + parseFloat(e.amountSpent || 0),
        0
      )
    : 0;

  const presupuestoCalculado = Array.isArray(raw?.orderDetails)
    ? raw.orderDetails.reduce((acc: number, item: any) => {
        const qty = parseFloat(item.quantity || 0);
        const cost = parseFloat(/*item.unitCost || */item.unitPrice || 0);
        return acc + qty * cost;
      }, 0)
    : 0;

  return {
    id: raw?.id ? Number(raw.id) : 0,
    folio:
      raw?.code && raw.code !== "ODC-"
        ? raw.code
        : `ODC-${raw?.id || "S/F"}`,

    created: raw?.created ?? null,
    updated: raw?.updated ?? null,

    subtotal: 0,//raw?.subtotal ? parseFloat(raw.subtotal) : presupuestoCalculado,
    tax: 0,//raw?.taxAmount ? parseFloat(raw.taxAmount) : 0,
    total: raw?.totalAmount ? parseFloat(raw.totalAmout) : presupuestoCalculado,

    totalEstimado: totalGastado,
    presupuestoTotal: presupuestoCalculado,

    statusName: raw?.status?.name || "Desconocido",

    customerName: raw?.customer?.name || raw?.name || "Sin asignar",
    plate: raw?.fleetVehicle?.plateNumber || "",

    items: Array.isArray(raw?.orderDetails)
      ? raw.orderDetails.map((item: any) => {
          const q = parseFloat(item.quantity || 0);
          const p = parseFloat(item.unitPrice || 0);

          return {
            id: item.id,
            tipo: item.productType?.name,
            nombre: item.product?.name || item.name || "Producto/Servicio",
            barcode: item.product?.barcode,
            cantidad: q,
            precioUnitario: p,
            totalItem: q * p,
            productTypeId: item.productTypeId,
            productId: item.productId,
            supplierId: item.supplierId
          };
        })
      : [],

    expenses: Array.isArray(raw?.expenses)
      ? raw.expenses.map((e: any) => ({
          id: Number(e.id),
          code: e.code,
          amountSpent: parseFloat(e.amountSpent || 0),
          created: e.created,
          bankId: e.bankId,
          supplierId: e.supplierId,
          evidenceFile:
            e.evidenceFile ||
            e.evidence_file ||
            e.receiptFile ||
            e.receipt_file ||
            undefined,
        }))
      : [],
  };
}

export function normalizeBanks(raw: any): Banco[] {
  const data = Array.isArray(raw) ? raw : raw?.data || [];

  return data
    .map((b: any) => ({
      id: String(b?.id || ""),
      nombre: String(b?.name || b?.nombre || ""),
      cuenta: String(b?.account || b?.accountNumber || ""),
    }))
    .filter((b: any) => b.id);
}

export function normalizeSuppliers(raw: any): Proveedor[] {
  const data = Array.isArray(raw) ? raw : raw?.data || [];

  return data
    .map((s: any) => ({
      id: String(s?.id || ""),
      nombre: String(s?.name || s?.nombre || ""),
      codigo: s?.code || "",
      tipo: s?.supplierType?.name || "",
      activo: s?.isActive ?? true,
      creado: s?.created,
      actualizado: s?.updated,
      organizationId: s?.organizationId
        ? String(s?.organizationId)
        : undefined,
    }))
    .filter((s: any) => s.id);
}

export function normalizeEmployees(raw: any): Empleado[] {
  const mapOne = (e: any): Empleado => ({
    id: pickId(e),
    nombre: String(e?.nombre ?? e?.name ?? e?.employee ?? e?.empleado ?? ""),
  });

  return firstArrayInObject(raw)
    .map(mapOne)
    .filter((e) => e.id);
}

function extractTokenFromData(data: any): string | null {
  if (!data) return null;

  const candidates = [
    data.access_token,
    data.accessToken,
    data.token,
    data?.data?.access_token,
    data?.data?.accessToken,
    data?.data?.token,
  ].filter(Boolean);

  if (candidates.length) return String(candidates[0]);

  const auth = data?.headers?.authorization || data?.headers?.Authorization;
  if (
    auth &&
    typeof auth === "string" &&
    auth.toLowerCase().startsWith("bearer ")
  ) {
    return auth.slice(7);
  }

  return null;
}

export async function login(
  username: string,
  password: string,
  _remember?: boolean
) {
  const url = "/auth/login";

  console.log("[api.login] → POST", API_URL + url, {
    usernameMasked: username.replace(/(.{2}).+(@.*)/, "$1***$2"),
    bodyKeys: ["username", "password"],
  });

  let res;

  try {
    res = await api.post(
      url,
      { username, password },
      { headers: { "Content-Type": "application/json" } }
    );
  } catch (err: any) {
    const flat = formatAxiosError(err);
    console.error("[api.login] × request error", flat);
    throw err;
  }

  console.log(
    "[api.login] ← status",
    res.status,
    "keys:",
    Object.keys(res.data || {})
  );

  const rawToken =
    extractTokenFromData(res.data) || extractTokenFromData(res) || null;

  if (!rawToken) {
    console.warn(
      "[api.login] No se encontró token en la respuesta. data:",
      res.data
    );
    throw new Error(
      "No se encontró el token en la respuesta. Esperaba access_token / token."
    );
  }

  setToken(rawToken);
  console.log("[api.login] token guardado en sessionStorage (oculto)");
  scheduleAutoLogout(rawToken);

  return { access_token: rawToken } as const;
}

export function logout() {
  console.log("[api.logout] limpiando token");
  clearToken();
}

async function getWithParams<T>(url: string, params?: any) {
  const res = await api.get<T>(url, { params });

  console.log("===== BACKEND RESPONSE =====");
  console.log("METHOD: GET");
  console.log("URL:", `${API_URL}${url}`);
  console.log("PARAMS:", JSON.stringify(params, null, 2));
  console.log("RESPONSE:", JSON.stringify((res as any).data, null, 2));
  console.log("===== END BACKEND RESPONSE =====");

  return res.data;
}

export async function getOrdersNormalized(): Promise<OC[]> {
  //const data = await getWithParams<any>("/orders/0?classes='purchase'");
  const data = await getWithParams<any>("/purchaseOrders/0");
  const list = Array.isArray(data) ? data : data?.data || data?.orders || [];
  return list.map(normalizeOrder);
}

export async function getOrderByIdNormalized(id: number): Promise<OC> {
  //const data = await getWithParams<any>(`/orders/${id}?classes='purchase'`);
  const data = await getWithParams<any>(`/purchaseOrders/${id}`);
  const raw = Array.isArray(data) ? data[0] : data;
  return normalizeOrder(raw);
}

export async function getOrders() {
  //return getWithParams<any>("/orders/0?classes='purchase'");
  return getWithParams<any>("/purchaseOrders/0");
}

export async function getOrderById(id: number) {
  //return getWithParams<any>(`/orders/${id}?classes='purchase'`);
  return getWithParams<any>(`/purchaseOrders/${id}`);
}

/* =========================================
  Gastos
  ========================================= */
export type ExpenseCreateBody = {
  orderId: number;
  bankId: number;
  supplierId: number;
  productTypeId: number;
  productId: number;
  quantity: number;
  unitPrice: number;
  amountSpent: number;
  code: string;
  orderDetailId?: number | null;

  invoicePdf?: string;
  receiptFile?: string;
  evidenceFile?: string;
  invoiceXml?: string;
};

export type ProviderCreateBody = {
  code: string;
  name: string;
  supplierTypeId: number;
  version: number;
};

export const createExpense = (body: ExpenseCreateBody) =>
  api.post("/expenses", body, {
    headers: { "Content-Type": "application/json" },
  });

// Se deja exportada solo para no romper imports viejos,
// pero ya NO debe usarse porque el backend indicó que esa ruta no existe.
export const uploadExpenseFiles = (
  _expenseId: number,
  _files: {
    invoicePdf?: File | Blob;
    invoiceXml?: File | Blob;
    receiptFile?: File | Blob;
    evidenceFile?: File | Blob;
  }
) =>
  Promise.reject(
    new Error(
      "El endpoint /expenses/:id/evidences ya no existe. Ahora los archivos deben enviarse dentro de POST /expenses usando invoicePdf, receiptFile, evidenceFile e invoiceXml."
    )
  );

export const getProductsRaw = (productTypeId: string) => api.get("/products/0/names?productTypeId=" + productTypeId);

export const getProducts = async (productTypeId: string) => {
  const { data } = await getProductsRaw(productTypeId);
  return data;
};

export const getProductTypesRaw = () => api.get("/productTypes/0/names");

export const getProductTypes = async () => {
  const { data } = await getProductTypesRaw();
  return data;
};

export const getPurchaseCategoriesRaw = () => api.get("/purchaseCategories/0/names");

export const getPurchaseCategories = async () => {
  const { data } = await getPurchaseCategoriesRaw();
  return data;
};

export const getSupplierTypesRaw = () => api.get("/supplierTypes/0/names");

export const getSupplierTypes = async () => {
  const { data } = await getSupplierTypesRaw();
  return data;
};

export const getBanksRaw = () => api.get("/banks/0");

export const getBanks = async () => {
  const { data } = await getBanksRaw();
  return normalizeBanks(data);
};

export const createBank = (_nombre: string, _cuenta: string) =>
  Promise.reject(
    new Error("Aún no existe endpoint para CREAR bancos en el backend.")
  );

export const updateBank = (_id: string, _data: Partial<Banco>) =>
  Promise.reject(
    new Error("Aún no existe endpoint para ACTUALIZAR bancos en el backend.")
  );

export const deleteBank = (_id: string) =>
  Promise.reject(
    new Error("Aún no existe endpoint para ELIMINAR bancos en el backend.")
  );

export const createProvider = (body: ProviderCreateBody) =>
  api.post("/suppliers", body, {
    headers: { "Content-Type": "application/json" },
  });

export const getSuppliersRaw = () => api.get("/suppliers/0");

export const getSuppliers = async (): Promise<Proveedor[]> => {
  const { data } = await getSuppliersRaw();
  return normalizeSuppliers(data);
};

export const createSupplier = (_nombre: string) =>
  Promise.reject(
    new Error("Aún no existe endpoint para CREAR proveedores en el backend.")
  );

export const updateSupplier = (_id: string, _data: Partial<Proveedor>) =>
  Promise.reject(
    new Error("Aún no existe endpoint para ACTUALIZAR proveedores en el backend.")
  );

export const deleteSupplier = (_id: string) =>
  Promise.reject(
    new Error("Aún no existe endpoint para ELIMINAR proveedores en el backend.")
  );

export const getEmployeesRaw = () => api.get("/employees/0");

export const getEmployees = async () => {
  const { data } = await getEmployeesRaw();
  return normalizeEmployees(data);
};

export default api;